import { useState, useEffect, useCallback } from 'react';
import { Users, Plus, X, Mail, Shield, Crown, UserMinus, Loader2, Copy, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const roleConfig = {
  owner: { icon: Crown, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Owner' },
  admin: { icon: Shield, color: 'text-[#2563eb]', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Admin' },
  member: { icon: Users, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', label: 'Member' },
};

export default function TeamPage() {
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(null);
  const [myRole, setMyRole] = useState('member');

  const fetchData = useCallback(async () => {
    try {
      const { data: ws } = await api.get('/workspaces/current');
      setWorkspace(ws);
      const role = ws.your_role || 'member';
      setMyRole(role);
      setMembers(ws.members || []);
      
      // Check if user can manage (owner, admin, org_admin, manager roles)
      const managerRoles = ['owner', 'admin', 'org_admin', 'manager', 'account_head', 'delivery_head'];
      if (managerRoles.includes(role)) {
        const { data: inv } = await api.get(`/workspaces/${ws.workspace_id}/invites`);
        setInvites(inv);
      }
    } catch (err) { 
      logger.error('Team fetch error:', err); 
    } finally { 
      setLoading(false); 
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sendInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/workspaces/${workspace.workspace_id}/invite`, { email: inviteEmail, role: inviteRole });
      setShowInvite(false);
      setInviteEmail('');
      toast.success('Invitation sent!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally { setSending(false); }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await api.delete(`/workspaces/${workspace.workspace_id}/members/${userId}`);
      toast.success('Member removed');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove member'); }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await api.delete(`/workspaces/${workspace.workspace_id}/invites/${inviteId}`);
      toast.success('Invite cancelled');
      fetchData();
    } catch (err) { logger.error(err); }
  };

  const resendInvite = async (inviteId) => {
    try {
      await api.post(`/workspaces/${workspace.workspace_id}/invites/${inviteId}/resend`);
      toast.success('Invitation resent! New 7-day link generated.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const copyInviteLink = (token) => {
    const url = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Link copied!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <Loader2 className="w-8 h-8 text-[#2563eb] animate-spin" />
      </div>
    );
  }

  const canManage = ['owner', 'admin', 'org_admin', 'manager', 'account_head', 'delivery_head'].includes(myRole);

  return (
    <div className="flex-1 overflow-auto bg-transparent p-6 sm:p-8 relative" data-testid="team-page" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Owner Welcome Banner */}
      {myRole === 'owner' && members.length === 1 && (
        <div className="bg-gradient-to-r from-[#1e40af] to-[#3b82f6] rounded-xl p-6 mb-6 text-white shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-2">👋 Welcome to Your Workspace!</h3>
              <p className="text-blue-100 text-sm leading-relaxed mb-4">
                You're the workspace owner with full admin access. Start by inviting your recruiting team — they'll receive an email with a secure invite link.
              </p>
              <button
                onClick={() => setShowInvite(true)}
                className="bg-white text-[#2563eb] px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Invite Your First Team Member
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white/90 backdrop-blur-xl border border-slate-200/60 rounded-3xl shadow-xl p-6 mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Team</h1>
          <p className="text-slate-500 text-sm mt-1">
            {workspace?.name} &middot; {members.length} member{members.length !== 1 ? 's' : ''}
          </p>
        </div>
        {canManage && (
          <button
            data-testid="invite-member-btn"
            onClick={() => setShowInvite(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-emerald-600 hover:shadow-lg hover:-translate-y-0.5 transition-all text-white font-bold px-5 py-2.5 rounded-xl"
          >
            <Plus className="w-4 h-4" /> Invite Member
          </button>
        )}
      </div>

      <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl shadow-lg overflow-hidden mb-6">
        <div className="px-5 py-3 bg-slate-50/80 border-b border-slate-200/60">
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Team Members</h2>
        </div>
        {members.map((member, i) => {
          const rc = roleConfig[member.role] || roleConfig.member;
          const RoleIcon = rc.icon;
          return (
            <div
              key={member.user_id}
              data-testid={`member-row-${member.user_id}`}
              className="flex items-center justify-between px-6 py-4 border-b border-slate-200/50 hover:bg-slate-50/80 transition-colors last:border-0"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-emerald-50 text-blue-700 font-bold flex items-center justify-center shadow-sm">
                  {member.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="text-slate-800 font-semibold text-sm">{member.name}</p>
                  <p className="text-slate-400 text-xs">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${rc.bg} ${rc.color} border ${rc.border}`}>
                  <RoleIcon className="w-3 h-3" />
                  {rc.label}
                  {member.is_you && <span className="text-[10px] opacity-70">(you)</span>}
                </span>
                {canManage && !member.is_you && member.role !== 'owner' && (
                  <button
                    data-testid={`remove-member-${member.user_id}`}
                    onClick={() => removeMember(member.user_id)}
                    className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove member"
                  >
                    <UserMinus className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pending Invites */}
      {canManage && invites.length > 0 && (
        <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl shadow-lg overflow-hidden">
          <div className="px-5 py-3 bg-slate-50/80 border-b border-slate-200/60">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Pending Invites ({invites.length})</h2>
          </div>
          {invites.map(invite => (
            <div key={invite.invite_id} className="flex items-center justify-between px-6 py-4 border-b border-slate-200/50 last:border-0 hover:bg-slate-50/80 transition-colors">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-700">{invite.email}</p>
                  <p className="text-xs text-slate-400 capitalize">{invite.role}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => resendInvite(invite.invite_id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-[#2563eb] hover:bg-blue-50 transition-colors font-medium"
                  title="Resend invite (generates new 7-day link)"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Resend
                </button>
                <button
                  onClick={() => copyInviteLink(invite.token)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  {copied === invite.token ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  Copy Link
                </button>
                <button
                  onClick={() => cancelInvite(invite.invite_id)}
                  className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowInvite(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-7 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="invite-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-slate-800">Invite Team Member</h2>
              <button onClick={() => setShowInvite(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={sendInvite} className="space-y-4">
              <div>
                <label className="text-slate-600 text-sm font-medium block mb-1.5">Email Address</label>
                <input
                  data-testid="invite-email-input"
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  required
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none text-sm"
                  placeholder="colleague@company.com"
                />
              </div>
              <div>
                <label className="text-slate-600 text-sm font-medium block mb-1.5">Role</label>
                <select
                  data-testid="invite-role-select"
                  value={inviteRole}
                  onChange={e => setInviteRole(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none text-sm"
                >
                  <option value="recruiter">Recruiter - Create jobs, source & score candidates, schedule interviews</option>
                  <option value="senior_recruiter">Senior Recruiter - All recruiter access + advanced analytics</option>
                  <option value="team_lead">Team Lead - Lead recruiting team + assign candidates</option>
                  <option value="manager">Manager - Team management + workspace settings</option>
                  <option value="admin">Admin - Full access except ownership (legacy)</option>
                  <option value="member">Member - Same as Recruiter (legacy)</option>
                </select>
                <p className="text-xs text-slate-500 mt-2">
                  <strong>Note:</strong> "Member" and "Recruiter" have the same permissions — full access to jobs, sourcing, scoring, and scheduling.
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowInvite(false)} className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl text-sm hover:bg-slate-50 transition-colors">Cancel</button>
                <button
                  data-testid="send-invite-btn"
                  type="submit"
                  disabled={sending}
                  className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
